import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, TOKEN_KEY, IMPERSONATION_TOKEN_KEY, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // null = checking, false = unauthenticated, user object = authenticated
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);
  // Phase 12 — impersonation state. When set, "user" is the target user and
  // impersonating.admin is the admin who initiated view-as. Persisted so a
  // page refresh preserves the impersonation session.
  const [impersonating, setImpersonating] = useState(() => {
    try {
      const raw = localStorage.getItem("belomotors_impersonation_meta");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me", {
        // Swallow expected 401s (unauth users) without console spam
        validateStatus: (s) => s < 500,
      });
      if (data && data.id) {
        setUser(data);
        return data;
      }
      setUser(false);
      return null;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth callback, let AuthCallback handle it
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setInitializing(false);
      return;
    }
    refresh().finally(() => setInitializing(false));
  }, [refresh]);

  const loginWithPassword = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const signup = async (payload) => {
    try {
      const { data } = await api.post("/auth/signup", payload);
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const exchangeGoogleSession = async (sessionId) => {
    try {
      const { data } = await api.post("/auth/google/session", { session_id: sessionId });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    localStorage.removeItem("belomotors_impersonation_meta");
    setImpersonating(null);
    setUser(false);
  };

  // Phase 12 — impersonation.
  const startImpersonation = async (targetUserId) => {
    try {
      const { data } = await api.post(`/admin/users/${targetUserId}/impersonate`);
      // Save the target's JWT under a separate localStorage key so the
      // interceptor picks it up automatically. Admin token stays under TOKEN_KEY.
      localStorage.setItem(IMPERSONATION_TOKEN_KEY, data.access_token);
      const meta = { admin: data.impersonating_admin, target: data.user, started_at: Date.now() };
      localStorage.setItem("belomotors_impersonation_meta", JSON.stringify(meta));
      setImpersonating(meta);
      setUser(data.user);
      return { ok: true, targetRole: data.user.role };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const endImpersonation = async () => {
    try { await api.post("/admin/impersonation/end"); } catch (_) {}
    localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    localStorage.removeItem("belomotors_impersonation_meta");
    setImpersonating(null);
    // Re-fetch the admin's identity (interceptor now falls back to TOKEN_KEY).
    await refresh();
  };

  return (
    <AuthContext.Provider
      value={{
        user: user && user !== false ? user : null,
        isAuthenticated: !!user && user !== false,
        initializing,
        refresh,
        loginWithPassword,
        signup,
        exchangeGoogleSession,
        logout,
        impersonating,
        startImpersonation,
        endImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
